import useNRQuery from '../../../graphql/useNRQuery';
import NRVulnerabilityDescription from './NRVulnerabilityDescription/NRVulnerabilityDescription';
import CircularProgress from '@mui/material/CircularProgress';
import { createContext, useEffect, useState, useRef } from 'react';
import { VULNERABILITY } from '../../../graphql/queries/vulnerability';
import NRReferences from './NRReferences/NRReferences';
import NRKnownExploits from './NRKnownExploits/NRKnownExploits';
import NRSeverityAndMetrics from './NRSeverityAndMetrics/NRSeverityAndMetrics';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { CONSTANTS } from '../../../constants/constants';
import Alert from '@mui/material/Alert';
import NRCISAKevAlertDetails from './NRCISAKevDetails/NRCISAKevDetails';
import { useTheme } from '@mui/material/styles';
import FixVersionIcon from '../../../NRIcons/FixVersionIcon';
import EllipsisTypography from '../../EllipsisTypography/EllipsisTypography';

const { TABS, ALERT } = CONSTANTS.TABLE.VULNERABILITIES_TABLE.VULNERABILITY_DETAIL_DRAWER;

export const VulnDrawerTabsContext = createContext({
  currentTab: null,
  setCurrentTab: () => {},
  tabOrder: null,
  scrollSpyTabsValue: null,
  setScrollSpyTabsValue: () => {},
  setTabClicked: () => {},
  tabClicked: false
});

const DRAWER_HEADER_HEIGHT = 64 + 48 + 64; // header, tabs, footer

const SCROLL_TO_FOCUS_DELAY_MS = 850;

function NRVulnerabilityDetails({ row }) {
  const { cve, isReachable, fixVersions } = row;
  const [currentTab, setCurrentTab] = useState('DESCRIPTION');
  const [tabOrder] = useState(['DESCRIPTION', 'CVSS_METRICS', 'KNOWN_EXPLOITS', 'REFERENCES']);
  const [scrollSpyTabsValue, setScrollSpyTabsValue] = useState([true, false, false, false]);
  const [knownExploitsCount, setKnownExploitsCount] = useState(null);
  const [hasKnownExploitedVulnerabilities, setHasKnownExploitedVulnerabilities] = useState(false);
  const [scrollableElement, setScrollableElement] = useState(null);
  const [scrollThreshold, setScrollThreshold] = useState([0, 0, 0, 0]);
  const [tabClicked, setTabClicked] = useState(false);
  const isScrollingProgrammatically = useRef(false);
  const knownExploitsRegionRef = useRef(null);

  const theme = useTheme();

  const inlineAlertLinkButtonSx = {
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'inline',
    verticalAlign: 'baseline',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  };

  const [cveDetails, cveDetailsLoading] = useNRQuery({
    query: VULNERABILITY,
    options: { variables: { args: { id: cve } }, context: { version: 'v3' } },
    qlObjKey: 'vulnerability',
    transformData: data => {
      return {
        ...data,
        impactV4: data?.impactV4
          ? {
              base: {
                ...data?.impactV4?.cvssData,
                ...data?.impactV4?.threatCvssSecondary,
                ...data?.impactV4?.threatCvssSecondary?.associatedBaseMetricV40
              }
            }
          : undefined,
        impact: data?.impactV31
          ? {
              base: { ...data?.impactV31?.metric, ...data?.impactV31?.metric?.cvssData }
            }
          : data?.impact,
        exploit: {
          ...data?.exploit,
          references: {
            ...data?.exploit?.references,
            knownExploitedVulnerabilities: data?.exploit?.references?.knownExploitedVulnerabilities
              ? Array.isArray(data?.exploit?.references?.knownExploitedVulnerabilities)
                ? data?.exploit?.references?.knownExploitedVulnerabilities
                : [data?.exploit?.references?.knownExploitedVulnerabilities]
              : null
          }
        }
      };
    }
  });

  const scrollToPosition = position => {
    if (!scrollableElement) return;
    scrollableElement.scrollTo({
      top: position,
      behavior: 'smooth'
    });
  };

  const focusAfterScroll = focusTarget => {
    if (!focusTarget) return;
    window.setTimeout(() => {
      if (typeof focusTarget === 'function') {
        focusTarget();
      } else if (focusTarget.current) {
        focusTarget.current.focus({ preventScroll: true });
      }
    }, SCROLL_TO_FOCUS_DELAY_MS);
  };

  const vulnerabilityDescriptionScrollIntoView = () => scrollToPosition(getScrollIndex(0));
  const severityAndMetricsScrollIntoView = () => scrollToPosition(getScrollIndex(1));
  const knownExploitsScrollIntoView = () => scrollToPosition(getScrollIndex(2));
  const referencesScrollIntoView = () => scrollToPosition(getScrollIndex(3));

  const scrollKnownExploitsFromAlert = () => {
    scrollToPosition(getScrollIndex(2));
    focusAfterScroll(knownExploitsRegionRef);
  };

  const scrollToReachabilityEvidence = () => {
    scrollToPosition(getScrollIndex(1.5));
    focusAfterScroll(() => document.getElementById('NRVulnerabilityReachability')?.focus({ preventScroll: true }));
  };

  const handleChange = (event, newValue) => {
    isScrollingProgrammatically.current = true;
    switch (newValue) {
      case 'DESCRIPTION':
        vulnerabilityDescriptionScrollIntoView();
        break;
      case 'CVSS_METRICS':
        severityAndMetricsScrollIntoView();
        break;
      case 'KNOWN_EXPLOITS':
        knownExploitsScrollIntoView();
        break;
      case 'REFERENCES':
        referencesScrollIntoView();
        break;
      default:
        break;
    }
    setTabClicked(true);
    setCurrentTab(newValue);

    setTimeout(() => {
      isScrollingProgrammatically.current = false;
      setTabClicked(false);
    }, 800);
  };

  useEffect(() => {
    !!cveDetails &&
      setKnownExploitsCount(
        (cveDetails.exploit?.references?.exploits?.length || 0) +
          (cveDetails.exploit?.references?.threatActors?.length || 0) +
          (cveDetails.exploit?.references?.botnets?.length || 0) +
          (cveDetails.exploit?.references?.knownAttacks?.length || 0)
      );
    !!cveDetails && setHasKnownExploitedVulnerabilities(cveDetails.exploit?.references?.knownExploitedVulnerabilities);
  }, [cveDetails]);

  useEffect(() => {
    if (cveDetailsLoading) return;
    const scrollableElem = document.querySelector('#vuln-details-main-container');
    if (scrollableElem) setScrollableElement(scrollableElem);
  }, [cveDetailsLoading]);

  useEffect(() => {
    setScrollThreshold([getScrollIndex(0), getScrollIndex(1), getScrollIndex(2), getScrollIndex(3)]);
  }, [scrollableElement?.scrollHeight]);

  const handleSetScrollSpyTabsValue = (index, value) => {
    if (!scrollableElement) return;

    const isAtBottom = scrollableElement.scrollHeight - scrollableElement.scrollTop <= scrollableElement.clientHeight + 50; // 50px threshold

    if (isAtBottom) {
      setScrollSpyTabsValue(prev => prev.map((_, i) => i === prev.length - 1));
      return;
    }

    setScrollSpyTabsValue(prev => {
      let a = [...prev];
      a[index] = value;
      return a;
    });
  };

  useEffect(() => {
    if (!scrollableElement) return;

    let scrollTimeout;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) {
        return;
      }

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50;
        const isAtTop = scrollTop === 0;

        let updatedSpyValues = scrollThreshold.map(threshold => scrollTop >= threshold);

        if (isAtBottom && !isScrollingProgrammatically.current) {
          updatedSpyValues = scrollThreshold.map((_, idx) => idx === scrollThreshold.length - 1);
        } else if (isAtTop) {
          updatedSpyValues = scrollThreshold.map((_, idx) => idx === 0);
        }

        setScrollSpyTabsValue(updatedSpyValues);
      }, 50);
    };

    scrollableElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [scrollableElement, scrollThreshold]);

  useEffect(() => {
    if (!tabClicked) {
      let scrolledIndex = scrollSpyTabsValue.findLastIndex(elem => elem === true);
      setCurrentTab(tabOrder[scrolledIndex !== -1 ? scrolledIndex : 0]);
    }
  }, [scrollSpyTabsValue]);

  function getScrollIndex(index) {
    if (!scrollableElement) return 0;

    const knownExploitsAlertHeight = hasKnownExploitedVulnerabilities ? cisaKevDetailsComponentHeight : 0;
    const alertOffset =
      (fixVersions?.length ? alertComponentHeight : 0) + (knownExploitsCount ? alertComponentHeight : 0) + (isReachable ? alertComponentHeight : 0);

    const totalOffset = knownExploitsAlertHeight + alertOffset;

    const NRVulnerabilityDescriptionContainer = document.querySelector('#NRVulnerabilityDescription');
    const NRVulnerabilityReachabilityContainer = document.querySelector('#NRVulnerabilityReachability');
    const NRSeverityAndMetricsContainer = document.querySelector('#NRSeverityAndMetrics');
    const NRKnownExploitsContainer = document.querySelector('#NRKnownExploits');
    let prevComponentsHeight = 0;
    switch (index) {
      case 0:
        return 0;
      case 1:
        prevComponentsHeight = totalOffset + NRVulnerabilityDescriptionContainer?.clientHeight;
        return prevComponentsHeight;
      case 1.5:
        prevComponentsHeight = totalOffset + NRVulnerabilityReachabilityContainer?.clientHeight;
        return prevComponentsHeight;
      case 2:
        prevComponentsHeight = totalOffset + NRVulnerabilityDescriptionContainer?.clientHeight + NRSeverityAndMetricsContainer?.clientHeight;
        return prevComponentsHeight;
      case 3:
        prevComponentsHeight =
          totalOffset +
          NRVulnerabilityDescriptionContainer?.clientHeight +
          NRSeverityAndMetricsContainer?.clientHeight +
          NRKnownExploitsContainer?.clientHeight;
        return prevComponentsHeight;
      default:
        return 0;
    }
  }

  const VulnDrawerTabsDefaultValues = {
    currentTab: currentTab,
    setCurrentTab: setCurrentTab,
    tabOrder: tabOrder,
    scrollSpyTabsValue,
    setScrollSpyTabsValue: handleSetScrollSpyTabsValue,
    setTabClicked: setTabClicked,
    tabClicked: tabClicked
  };

  const alertComponentHeight = 40 + 24;
  const cisaKevDetailsComponentHeight = 188 + 24;
  return (
    <Box role="dialog" aria-modal="false" aria-label={`Vulnerability details for ${cve}`}>
      <Tabs value={currentTab} onChange={handleChange} textColor="inherit" variant="fullWidth" aria-label="drawer tabs">
        <Tab
          label={<Typography variant={'label'} children={TABS.DESCRIPTION} />}
          key={TABS.DESCRIPTION}
          value={'DESCRIPTION'}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={TABS.CVSS_METRICS} />}
          key={TABS.CVSS_METRICS}
          value={'CVSS_METRICS'}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={TABS.KNOWN_EXPLOITS} />}
          key={TABS.KNOWN_EXPLOITS}
          value={'KNOWN_EXPLOITS'}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={TABS.REFERENCES} />}
          key={TABS.REFERENCES}
          value={'REFERENCES'}
          sx={{ borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
      </Tabs>
      <Box
        component={'div'}
        id={'vuln-details-main-container'}
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          padding: theme.spacing(3, 3, 2)
        }}
      >
        <VulnDrawerTabsContext.Provider value={VulnDrawerTabsDefaultValues}>
          {!cveDetailsLoading && !!cveDetails && (
            <>
              {!!fixVersions?.length && (
                <Alert icon={<FixVersionIcon />} severity="info" sx={{ mb: 3, height: theme.spacing(5), alignItems: 'center' }}>
                  <EllipsisTypography>
                    There is a{' '}
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      fix version
                    </Box>{' '}
                    available for this CVE’s component: {fixVersions.join(', ')}
                  </EllipsisTypography>
                </Alert>
              )}
              {!!knownExploitsCount && (
                <Alert severity="error" sx={{ mb: 3, height: theme.spacing(5), alignItems: 'center' }}>
                  <span>
                    {ALERT.MESSAGE}
                    <Box
                      component="button"
                      type="button"
                      sx={inlineAlertLinkButtonSx}
                      aria-label={`Go to ${TABS.KNOWN_EXPLOITS} section, ${knownExploitsCount} exploits`}
                      onClick={() => scrollKnownExploitsFromAlert()}
                    >
                      {TABS.KNOWN_EXPLOITS}
                    </Box>
                    {` (${knownExploitsCount})`}
                  </span>
                </Alert>
              )}
              {isReachable && (
                <Alert severity="warning" sx={{ mb: 3, height: theme.spacing(5), alignItems: 'center' }}>
                  <span>
                    {'This vulnerability is found on a '}
                    <Box
                      component="button"
                      type="button"
                      sx={inlineAlertLinkButtonSx}
                      aria-label="Go to Reachability evidence section"
                      onClick={() => scrollToReachabilityEvidence()}
                    >
                      {'Reachable'}
                    </Box>
                    {' component.'}
                  </span>
                </Alert>
              )}
              {!!hasKnownExploitedVulnerabilities && (
                <NRCISAKevAlertDetails data={cveDetails.exploit?.references?.knownExploitedVulnerabilities[0]} />
              )}
              <Box role="region" aria-label="Vulnerability description" tabIndex={-1} id="vuln-region-description">
                <NRVulnerabilityDescription
                  isReachable={isReachable}
                  vulnerabilityData={row}
                  cveDetails={cveDetails}
                  isLoading={cveDetailsLoading}
                  threshold={scrollThreshold[0]}
                  scrollTarget={scrollableElement}
                  index={0}
                  scrollableId={'NRVulnerabilityDescription'}
                />
              </Box>
              <Box role="region" aria-label="Severity and CVSS metrics" tabIndex={-1} id="vuln-region-severity">
                <NRSeverityAndMetrics
                  vulnerabilityData={row}
                  cveDetails={cveDetails}
                  isLoading={cveDetailsLoading}
                  threshold={scrollThreshold[2]}
                  scrollTarget={scrollableElement}
                  index={2}
                  scrollableId={'NRSeverityAndMetrics'}
                />
              </Box>
              <Box ref={knownExploitsRegionRef} role="region" aria-label="Known exploits" tabIndex={-1} id="vuln-region-known-exploits">
                <NRKnownExploits
                  cveDetails={cveDetails}
                  isLoading={cveDetailsLoading}
                  threshold={scrollThreshold[3]}
                  scrollTarget={scrollableElement}
                  index={3}
                  scrollableId={'NRKnownExploits'}
                />
              </Box>
              <Box role="region" aria-label="References" tabIndex={-1} id="vuln-region-references">
                <NRReferences
                  cveDetails={cveDetails}
                  isLoading={cveDetailsLoading}
                  threshold={scrollThreshold[1]}
                  scrollTarget={scrollableElement}
                  index={1}
                  scrollableId={'NRReferences'}
                />
              </Box>
            </>
          )}
        </VulnDrawerTabsContext.Provider>
      </Box>
      {cveDetailsLoading && !cveDetails && (
        <Box
          sx={{
            minHeight: `calc(100vh - ${DRAWER_HEADER_HEIGHT}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
    </Box>
  );
}
export default NRVulnerabilityDetails;
